import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon, PencilIcon, DocumentIcon, FolderOpenIcon } from '@heroicons/react/24/outline';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const [event, setEvent] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    const { data } = await get(`/api/events/${id}`);
    if (data) {
      setEvent(data);
      
      // Load files if fileIds are present
      if (data.fileIds && data.fileIds.length > 0) {
        fetchFileDetails(data.fileIds);
      } else if (data.files && data.files.length > 0) {
        setFiles(data.files);
      }
    }
  };

  const fetchFileDetails = async (fileIds) => {
    if (!fileIds || fileIds.length === 0) return;
    
    setLoadingFiles(true);
    try {
      const filePromises = fileIds.map(fileId => get(`/api/files/${fileId}`));
      const responses = await Promise.all(filePromises);
      const fileData = responses.map(res => res.data).filter(Boolean);
      setFiles(fileData);
    } catch (error) {
      console.error('Error fetching file details:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  if (loading) return <LoadingSpinner />;
  if (!event) return <div className="p-6 text-gray-900 dark:text-white">Event not found</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/events/filter')}
            className="mr-4 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Event Details</h1>
        </div>
        <button
          onClick={() => navigate(`/events/edit/${id}`)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
        >
          <PencilIcon className="h-5 w-5" />
          <span>Edit</span>
        </button>
      </div>

      {/* Event Information */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">ID</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{event.id}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">UUID</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white font-mono text-sm">
              {event.evtUuid || event.uuid || '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Trip ID</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{event.tripId || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Event Type</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {event.eventType?.description || '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Reference Number</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{event.referenceNum || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Timestamp</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {event.timestamp ? new Date(event.timestamp).toLocaleString() : '-'}
            </p>
          </div>
          {event.voiceText && (
            <div className="md:col-span-2">
              <label className="text-sm text-gray-500 dark:text-gray-400">Voice Text</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white whitespace-pre-wrap bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                {event.voiceText}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Files Section - Multiple Files */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
            <FolderOpenIcon className="h-5 w-5 text-indigo-600" />
            <span>Associated Files ({files.length})</span>
          </h2>
        </div>

        {loadingFiles ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : files.length > 0 ? (
          <div className="space-y-3">
            {files.map((file) => (
              <div 
                key={file.id} 
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <DocumentIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {file.fileName || file.path?.split('/').pop() || `File #${file.id}`}
                    </p>
                    <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
                      <span>{formatFileSize(file.fileSize || file.size)}</span>
                      {file.fileType && <span>• {file.fileType.fileName || 'Unknown type'}</span>}
                      {file.isVideo && <span className="text-indigo-600">🎬 Video</span>}
                      {file.fileIsEncrypted && <span className="text-yellow-600">🔒 Encrypted</span>}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 flex-shrink-0 ml-4">
                  {file.filePath && (
                    <button
                      onClick={() => window.open(file.filePath, '_blank')}
                      className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                    >
                      View
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/files/${file.id}`)}
                    className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400 mb-2" />
            <p>No files associated with this event</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetails;